/// <reference types="@types/grecaptcha" />

type Grecaptcha = typeof window.grecaptcha;

let loadPromise: Promise<Grecaptcha> | null = null;
let loadedKey: string | null = null;

const SCRIPT_SELECTOR = 'script[data-recaptcha="v3"]';
const DEFAULT_TIMEOUT_MS = 10000;

function buildSrc(siteKey: string) {
  return `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
}

function getExistingScript() {
  return document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
}

function injectScript(siteKey: string, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const src = buildSrc(siteKey);

  const existing = getExistingScript();
  let script: HTMLScriptElement;

  if (existing) {
    if (existing.src !== src) {
      throw new Error(
        'reCAPTCHA script already exists with a different siteKey. '
        + 'reCAPTCHA v3 should be initialized once per page with a single key.',
      );
    }
    script = existing;
  } else {
    script = document.createElement('script');
    script.async = true;
    script.dataset.recaptcha = 'v3';
    script.src = src;
    document.head.appendChild(script);
  }

  return new Promise<Grecaptcha>((resolve, reject) => {
    const g0 = window.grecaptcha;
    if (g0) {
      g0.ready(() => resolve(g0));
      return;
    }

    let cleanup: () => void = () => {};

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('reCAPTCHA load timeout'));
    }, timeoutMs);

    const onError = () => {
      cleanup();
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    const onLoad = () => {
      const g = window.grecaptcha;
      if (!g) {
        cleanup();
        reject(new Error('reCAPTCHA script loaded, but grecaptcha is not available'));
        return;
      }

      g.ready(() => {
        cleanup();
        resolve(g);
      });
    };

    cleanup = () => {
      window.clearTimeout(timeoutId);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
  });
}

export function ensureRecaptcha(siteKey: string) {
  if (loadedKey && loadedKey !== siteKey) {
    throw new Error(
      'ensureRecaptcha called with different siteKey. '
      + 'Initialize reCAPTCHA v3 once per page with a single key.',
    );
  }

  if (loadPromise) return loadPromise;

  loadedKey = siteKey;

  loadPromise = injectScript(siteKey).catch((e) => {
    loadPromise = null;
    loadedKey = null;
    throw e;
  });

  return loadPromise;
}

export async function execRecaptcha(action: string) {
  if (!loadedKey) {
    throw new Error('reCAPTCHA is not initialized. Call ensureRecaptcha(siteKey) first.');
  }

  const grecaptcha = await ensureRecaptcha(loadedKey);

  if (typeof grecaptcha.execute !== 'function') {
    throw new TypeError('grecaptcha.execute is not ready');
  }

  return grecaptcha.execute(loadedKey, { action });
}
